import api from '../config/api';
import { IndividualMemberDto } from '../types/member';
import { InstitutionDto } from '../types/institution';
import { RegistrationPetitionDocument } from '../types/admin';

export type ApplicationStatusValue =
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'NeedsChanges';

export interface ApplicationStatusResponse {
  applicationReferenceNumber: string;
  type?: string;
  /** Backend may send number or string enum */
  status: number | ApplicationStatusValue;
  submittedAt?: string;
  reviewedAt?: string;
  adminFeedback?: string;
  /** @deprecated use adminFeedback */
  rejectionReason?: string;
  canEdit?: boolean;
}

export interface PublicApplicationEdit {
  applicationReferenceNumber: string;
  type: string;
  status: number | ApplicationStatusValue;
  adminFeedback?: string;
  canEdit: boolean;
  individual?: IndividualMemberDto;
  institution?: InstitutionDto;
  documents: RegistrationPetitionDocument[];
}

export interface PublicApplicationResubmitPayload {
  applicantNote?: string;
  // Individual
  firstNameEn?: string;
  firstNameGr?: string;
  lastNameEn?: string;
  lastNameGr?: string;
  title?: string | number;
  gender?: string | number;
  dateOfBirth?: string;
  phone?: string;
  mobile?: string;
  mobilePhone?: string;
  website?: string;
  contactPreference?: string | number;
  residentialAddressEn?: string;
  residentialAddressGr?: string;
  residentialCityEn?: string;
  residentialCityGr?: string;
  residentialPostalCode?: string;
  residentialPostalCodeEn?: string;
  residentialPostalCodeGr?: string;
  residentialCountryEn?: string;
  residentialCountryGr?: string;
  professionalPath?: string | number;
  isGraduateStudent?: boolean;
  isRetired?: boolean;
  isVolunteer?: boolean;
  isHonoraryMember?: boolean;
  membershipType?: string | number;
  organisationNameEn?: string;
  organisationNameGr?: string;
  professionalTitleEn?: string;
  professionalTitleGr?: string;
  professionalInstitutionNameEn?: string;
  professionalInstitutionNameGr?: string;
  professionalAddress?: string;
  professionalAddressEn?: string;
  professionalAddressGr?: string;
  professionalPostalCode?: string;
  professionalCity?: string;
  professionalCityEn?: string;
  professionalCityGr?: string;
  professionalEmail?: string;
  professionalPhone?: string;
  workSector?: string | number;
  studies?: string;
  mainCommittee?: string;
  additionalCommittees?: string;
  transferFromIcom?: string;
  previousCardNumber?: string;
  // Institution
  institutionNameEn?: string;
  institutionNameGr?: string;
  institutionPhone?: string;
  institutionEmail?: string;
  institutionAddressEn?: string;
  institutionAddressGr?: string;
  institutionCityEn?: string;
  institutionCityGr?: string;
  institutionPostalCodeEn?: string;
  institutionPostalCodeGr?: string;
  institutionCountryEn?: string;
  institutionCountryGr?: string;
  secondaryAddressEn?: string;
  secondaryAddressGr?: string;
  secondaryCityEn?: string;
  secondaryCityGr?: string;
  secondaryPostalCodeEn?: string;
  secondaryPostalCodeGr?: string;
  secondaryCountryEn?: string;
  secondaryCountryGr?: string;
  membershipCategory?: string | number;
  foundationDate?: string;
  institutionType?: string;
  administrativeStatus?: string | number;
  directorTitle?: string | number;
  directorFirstNameEn?: string;
  directorFirstNameGr?: string;
  directorLastNameEn?: string;
  directorLastNameGr?: string;
  directorDateOfBirth?: string;
  directorEmail?: string;
  directorPhone?: string;
  icomRepresentativeTitle?: string | number;
  icomRepresentativeFirstNameEn?: string;
  icomRepresentativeFirstNameGr?: string;
  icomRepresentativeLastNameEn?: string;
  icomRepresentativeLastNameGr?: string;
  icomRepresentativeDateOfBirth?: string;
  icomRepresentativePosition?: string;
  icomRepresentativeEmail?: string;
  icomRepresentativePhone?: string;
  icomContactPreference?: string | number;
  icomRepresentativeCommunicationLanguage?: string | number;
  declarationDate?: string;
  acceptedStatement?: boolean;
  representative1Title?: string | number;
  representative1FirstNameEn?: string;
  representative1FirstNameGr?: string;
  representative1LastNameEn?: string;
  representative1LastNameGr?: string;
  representative1DateOfBirth?: string;
  representative1PositionEn?: string;
  representative1PositionGr?: string;
  representative1Email?: string;
  representative1Phone?: string;
  representative1CommitteeId?: string;
  representative2Title?: string | number;
  representative2FirstNameEn?: string;
  representative2FirstNameGr?: string;
  representative2LastNameEn?: string;
  representative2LastNameGr?: string;
  representative2DateOfBirth?: string;
  representative2PositionEn?: string;
  representative2PositionGr?: string;
  representative2Email?: string;
  representative2Phone?: string;
  representative2CommitteeId?: string;
  representative3Title?: string | number;
  representative3FirstNameEn?: string;
  representative3FirstNameGr?: string;
  representative3LastNameEn?: string;
  representative3LastNameGr?: string;
  representative3DateOfBirth?: string;
  representative3PositionEn?: string;
  representative3PositionGr?: string;
  representative3Email?: string;
  representative3Phone?: string;
  representative3CommitteeId?: string;
  // Files
  cv?: File | null;
  professionalActivityDocuments?: File | null;
  studentId?: File | null;
  institutionDescriptionDocument?: File | null;
}

export function normalizeRegistrationStatus(
  status: number | string | undefined | null
): number {
  if (typeof status === 'number') return status;
  const map: Record<string, number> = {
    Pending: 1,
    Approved: 2,
    Rejected: 3,
    NeedsChanges: 4,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
  };
  return map[String(status)] ?? 1;
}

export const applicationStatusService = {
  getByReference: async (reference: string): Promise<ApplicationStatusResponse> => {
    const response = await api.get<ApplicationStatusResponse>('/public/application-status', {
      params: { reference: reference.trim() },
    });
    const data = response.data;
    return {
      ...data,
      adminFeedback: data.adminFeedback ?? data.rejectionReason,
      canEdit: data.canEdit ?? normalizeRegistrationStatus(data.status) === 4,
    };
  },

  getEditable: async (reference: string): Promise<PublicApplicationEdit> => {
    const response = await api.get<PublicApplicationEdit>('/public/application', {
      params: { reference: reference.trim() },
    });
    return response.data;
  },

  resubmit: async (
    reference: string,
    payload: PublicApplicationResubmitPayload
  ): Promise<ApplicationStatusResponse> => {
    const form = new FormData();
    const append = (key: string, value?: string | number | boolean | File | null) => {
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'boolean') {
        form.append(key, String(value));
        return;
      }
      form.append(key, value as string | Blob);
    };

    append('applicantNote', payload.applicantNote);
    append('firstNameEn', payload.firstNameEn);
    append('firstNameGr', payload.firstNameGr);
    append('lastNameEn', payload.lastNameEn);
    append('lastNameGr', payload.lastNameGr);
    append('title', payload.title);
    append('gender', payload.gender);
    append('dateOfBirth', payload.dateOfBirth);
    append('phone', payload.phone);
    append('mobile', payload.mobile);
    append('mobilePhone', payload.mobilePhone ?? payload.mobile);
    append('website', payload.website);
    append('contactPreference', payload.contactPreference);
    append('residentialAddressEn', payload.residentialAddressEn);
    append('residentialAddressGr', payload.residentialAddressGr);
    append('residentialCityEn', payload.residentialCityEn);
    append('residentialCityGr', payload.residentialCityGr);
    append('residentialPostalCode', payload.residentialPostalCode);
    append('residentialPostalCodeEn', payload.residentialPostalCodeEn ?? payload.residentialPostalCode);
    append('residentialPostalCodeGr', payload.residentialPostalCodeGr ?? payload.residentialPostalCode);
    append('residentialCountryEn', payload.residentialCountryEn);
    append('residentialCountryGr', payload.residentialCountryGr);
    append('professionalPath', payload.professionalPath);
    if (payload.isGraduateStudent !== undefined) append('isGraduateStudent', payload.isGraduateStudent);
    if (payload.isRetired !== undefined) append('isRetired', payload.isRetired);
    if (payload.isVolunteer !== undefined) append('isVolunteer', payload.isVolunteer);
    if (payload.isHonoraryMember !== undefined) append('isHonoraryMember', payload.isHonoraryMember);
    append('membershipType', payload.membershipType);
    append('organisationNameEn', payload.organisationNameEn);
    append('organisationNameGr', payload.organisationNameGr);
    append('professionalTitleEn', payload.professionalTitleEn);
    append('professionalTitleGr', payload.professionalTitleGr);
    append('professionalInstitutionNameEn', payload.professionalInstitutionNameEn);
    append('professionalInstitutionNameGr', payload.professionalInstitutionNameGr);
    append('professionalAddress', payload.professionalAddress);
    append('professionalAddressEn', payload.professionalAddressEn);
    append('professionalAddressGr', payload.professionalAddressGr);
    append('professionalPostalCode', payload.professionalPostalCode);
    append('professionalCity', payload.professionalCity);
    append('professionalCityEn', payload.professionalCityEn);
    append('professionalCityGr', payload.professionalCityGr);
    append('professionalEmail', payload.professionalEmail);
    append('professionalPhone', payload.professionalPhone);
    append('workSector', payload.workSector);
    append('studies', payload.studies);
    append('mainCommittee', payload.mainCommittee);
    append('additionalCommittees', payload.additionalCommittees);
    append('transferFromIcom', payload.transferFromIcom);
    append('previousCardNumber', payload.previousCardNumber);
    append('institutionNameEn', payload.institutionNameEn);
    append('institutionNameGr', payload.institutionNameGr);
    append('institutionPhone', payload.institutionPhone);
    append('institutionEmail', payload.institutionEmail);
    append('institutionAddressEn', payload.institutionAddressEn);
    append('institutionAddressGr', payload.institutionAddressGr);
    append('institutionCityEn', payload.institutionCityEn);
    append('institutionCityGr', payload.institutionCityGr);
    append('institutionPostalCodeEn', payload.institutionPostalCodeEn);
    append('institutionPostalCodeGr', payload.institutionPostalCodeGr);
    append('institutionCountryEn', payload.institutionCountryEn);
    append('institutionCountryGr', payload.institutionCountryGr);
    append('secondaryAddressEn', payload.secondaryAddressEn);
    append('secondaryAddressGr', payload.secondaryAddressGr);
    append('secondaryCityEn', payload.secondaryCityEn);
    append('secondaryCityGr', payload.secondaryCityGr);
    append('secondaryPostalCodeEn', payload.secondaryPostalCodeEn);
    append('secondaryPostalCodeGr', payload.secondaryPostalCodeGr);
    append('secondaryCountryEn', payload.secondaryCountryEn);
    append('secondaryCountryGr', payload.secondaryCountryGr);
    append('membershipCategory', payload.membershipCategory);
    append('foundationDate', payload.foundationDate);
    append('institutionType', payload.institutionType);
    append('administrativeStatus', payload.administrativeStatus);
    append('directorTitle', payload.directorTitle);
    append('directorFirstNameEn', payload.directorFirstNameEn);
    append('directorFirstNameGr', payload.directorFirstNameGr);
    append('directorLastNameEn', payload.directorLastNameEn);
    append('directorLastNameGr', payload.directorLastNameGr);
    append('directorDateOfBirth', payload.directorDateOfBirth);
    append('directorEmail', payload.directorEmail);
    append('directorPhone', payload.directorPhone);
    append('icomRepresentativeTitle', payload.icomRepresentativeTitle);
    append('icomRepresentativeFirstNameEn', payload.icomRepresentativeFirstNameEn);
    append('icomRepresentativeFirstNameGr', payload.icomRepresentativeFirstNameGr);
    append('icomRepresentativeLastNameEn', payload.icomRepresentativeLastNameEn);
    append('icomRepresentativeLastNameGr', payload.icomRepresentativeLastNameGr);
    append('icomRepresentativeDateOfBirth', payload.icomRepresentativeDateOfBirth);
    append('icomRepresentativePosition', payload.icomRepresentativePosition);
    append('icomRepresentativeEmail', payload.icomRepresentativeEmail);
    append('icomRepresentativePhone', payload.icomRepresentativePhone);
    append('icomContactPreference', payload.icomContactPreference);
    append('icomRepresentativeCommunicationLanguage', payload.icomRepresentativeCommunicationLanguage);
    append('declarationDate', payload.declarationDate);
    if (payload.acceptedStatement !== undefined) append('acceptedStatement', payload.acceptedStatement);
    append('representative1Title', payload.representative1Title);
    append('representative1FirstNameEn', payload.representative1FirstNameEn);
    append('representative1FirstNameGr', payload.representative1FirstNameGr);
    append('representative1LastNameEn', payload.representative1LastNameEn);
    append('representative1LastNameGr', payload.representative1LastNameGr);
    append('representative1DateOfBirth', payload.representative1DateOfBirth);
    append('representative1PositionEn', payload.representative1PositionEn);
    append('representative1PositionGr', payload.representative1PositionGr);
    append('representative1Email', payload.representative1Email);
    append('representative1Phone', payload.representative1Phone);
    append('representative1CommitteeId', payload.representative1CommitteeId);
    append('representative2Title', payload.representative2Title);
    append('representative2FirstNameEn', payload.representative2FirstNameEn);
    append('representative2FirstNameGr', payload.representative2FirstNameGr);
    append('representative2LastNameEn', payload.representative2LastNameEn);
    append('representative2LastNameGr', payload.representative2LastNameGr);
    append('representative2DateOfBirth', payload.representative2DateOfBirth);
    append('representative2PositionEn', payload.representative2PositionEn);
    append('representative2PositionGr', payload.representative2PositionGr);
    append('representative2Email', payload.representative2Email);
    append('representative2Phone', payload.representative2Phone);
    append('representative2CommitteeId', payload.representative2CommitteeId);
    append('representative3Title', payload.representative3Title);
    append('representative3FirstNameEn', payload.representative3FirstNameEn);
    append('representative3FirstNameGr', payload.representative3FirstNameGr);
    append('representative3LastNameEn', payload.representative3LastNameEn);
    append('representative3LastNameGr', payload.representative3LastNameGr);
    append('representative3DateOfBirth', payload.representative3DateOfBirth);
    append('representative3PositionEn', payload.representative3PositionEn);
    append('representative3PositionGr', payload.representative3PositionGr);
    append('representative3Email', payload.representative3Email);
    append('representative3Phone', payload.representative3Phone);
    append('representative3CommitteeId', payload.representative3CommitteeId);
    append('cv', payload.cv);
    append('professionalActivityDocuments', payload.professionalActivityDocuments);
    append('studentId', payload.studentId);
    append('institutionDescriptionDocument', payload.institutionDescriptionDocument);

    const response = await api.post<ApplicationStatusResponse>(
      '/public/application/resubmit',
      form,
      {
        params: { reference: reference.trim() },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },
};
